/***
 *
 *   COLOR INPUT
 *   A hex colour field: native swatch picker plus a text field, kept in sync.
 *
 *   Built for the per-project brand colour on the white-labelled public pages.
 *   Empty is VALID and means "use the Valetify default" — that is how a tenant
 *   reverts, so it must not be treated as a missing value.
 *
 *   The value is normalised to a lowercase six-digit hex here rather than left
 *   to the server. The native picker only ever emits that form, so normalising
 *   is what stops the two controls disagreeing when someone types `ABC` into
 *   the text side.
 *
 *   PROPS
 *   Standard form field props (see components/form/form.js), plus:
 *   resetText: label for the "use the default" button (optional)
 *   preview: (value) => node, rendered under the field. A render prop rather
 *            than a hardcoded swatch strip, so this input stays generic and
 *            whatever a caller derives from the colour is the caller's business.
 *
 **********/

import { Button, ClassHelper, Error, Label } from 'components/lib';
import { useTranslate } from 'app/translations.js';
import Style from './input.tailwind.js';
import ColorStyle from './color.tailwind.js';

const HEX = /^#[0-9a-f]{6}$/i;

// What the swatch shows when the field is empty. The picker needs a concrete
// value, and the stock --primary-600 is the colour an empty field actually
// renders as on the public page.
const PLACEHOLDER_SWATCH = '#4f46e5';

/*
 * normalise()
 * what the user typed -> a canonical hex, or the raw input if it is not one yet
 *
 * Kept lenient on purpose: it runs on every keystroke, and rewriting a
 * half-typed value would fight the person typing it.
 */
function normalise(value) {
    const trimmed = (value || '').trim().toLowerCase();

    if (!trimmed) return '';

    const hashed = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;

    // Expand #abc, but only once it is complete.
    if (/^#[0-9a-f]{3}$/.test(hashed))
        return `#${hashed[1]}${hashed[1]}${hashed[2]}${hashed[2]}${hashed[3]}${hashed[3]}`;

    return hashed;
}

export function ColorInput(props) {
    const { _t } = useTranslate();
    const error = props.errorMessage || _t('publicIntake.brand.colorInvalid');

    const value = props.value || '';
    const isHex = HEX.test(value);

    function change(raw) {
        const next = normalise(raw);

        // Empty means "revert to the default", so it is valid. Anything else
        // has to be a complete hex before the form will accept a save.
        const valid = next === '' ? true : HEX.test(next);

        props.onChange?.(props.name, next, valid);
    }

    const textStyle = ClassHelper(Style, {
        textbox: true,
        className: ColorStyle.text,
        error: props.valid === false,
        success: props.valid === true,
    });

    return (
        <div className={Style.input}>
            {props.label && (
                <Label text={props.label} required={props.required} for={props.name} />
            )}

            <div className={ColorStyle.row}>
                {/* type="color" always emits #rrggbb, which is why the text
                    side is the one that needs normalising, not this one. */}
                <input
                    type="color"
                    id={props.name}
                    name={props.name}
                    value={isHex ? value : PLACEHOLDER_SWATCH}
                    disabled={props.disabled}
                    className={ColorStyle.swatch}
                    aria-label={props.label}
                    onChange={(e) => change(e.target.value)}
                />

                <input
                    type="text"
                    inputMode="text"
                    spellCheck="false"
                    autoComplete="off"
                    name={`${props.name}_hex`}
                    value={value}
                    disabled={props.disabled}
                    className={textStyle}
                    placeholder={props.placeholder || PLACEHOLDER_SWATCH}
                    onChange={(e) => change(e.target.value)}
                />

                {value !== '' && (
                    <Button
                        variant="ghost"
                        size="sm"
                        text={props.resetText || _t('publicIntake.brand.colorReset')}
                        action={() => change('')}
                    />
                )}
            </div>

            {props.hint && <p className={ColorStyle.hint}>{props.hint}</p>}

            {props.preview && (
                <div className={ColorStyle.preview}>{props.preview(value)}</div>
            )}

            {props.valid === false && <Error message={error} />}
        </div>
    );
}
